import React from 'react';
import { ChevronDown, ArrowUpRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';

const hover = 'hover:bg-[hsl(var(--wp-command-hover))] active:bg-[hsl(var(--wp-command-pressed))]';
/** Keep keyboard focus / text selection inside the canvas text editor when clicking ribbon commands */
const keepFocus = (e) => { if (e.button === 0) e.preventDefault(); };

export const WithTip = ({ tip, children, side = 'bottom' }) => {
  if (!tip) return children;
  return (
    <Tooltip delayDuration={400}>
      {/* Tooltips open on hover only: when a popover/menu closes it hands focus back to its trigger, and a
          focus-opened tooltip would sit on top of the neighbouring ribbon commands (Publisher shows none). */}
      <TooltipTrigger asChild onFocus={(e) => e.preventDefault()}>{children}</TooltipTrigger>
      <TooltipContent side={side} className="max-w-[260px] bg-popover text-popover-foreground border border-border shadow-md text-xs pointer-events-none">{tip}</TooltipContent>
    </Tooltip>
  );
};

export const RibbonGroup = ({ label, children, onLauncher, launcherTestId, className = '', testId }) => (
  <div className={`relative flex flex-col justify-between px-2 pt-1.5 pb-0.5 shrink-0 ${className}`} data-testid={testId || `ribbon-group-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <div className="flex items-start gap-1 h-[66px]">{children}</div>
    <div className="mt-0.5 h-[16px] flex items-center justify-center gap-1 text-[11px] leading-none text-muted-foreground select-none">
      <span>{label}</span>
      {onLauncher && (
        <button type="button" onClick={onLauncher} className={`absolute bottom-1 right-1 h-4 w-4 rounded-sm flex items-center justify-center ${hover} focus-visible:ring-2 focus-visible:ring-ring`} title={`${label} options`} data-testid={launcherTestId || `ribbon-group-${(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-dialog-launcher`}>
          <ArrowUpRight className="h-3 w-3" />
        </button>
      )}
    </div>
  </div>
);

export const RibbonSeparator = () => <div className="w-px self-stretch bg-border/70 my-1" />;

export const LargeButton = React.forwardRef(({ icon: Icon, label, onClick, active, disabled, tip, testId, chevron, children, className = '', ...rest }, ref) => (
  <WithTip tip={tip}>
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseDown={keepFocus}
      disabled={disabled}
      className={`group relative flex w-[58px] h-[64px] flex-col items-center justify-start rounded-md px-1 pt-1.5 transition-colors duration-150 ${hover} disabled:opacity-40 disabled:cursor-not-allowed ${active ? 'bg-primary/15 ring-1 ring-primary/40' : ''} ${className}`}
      data-testid={testId}
      data-state={active ? 'on' : 'off'}
      {...rest}
    >
      {Icon && <Icon className="h-6 w-6 text-foreground" strokeWidth={1.75} />}
      {children}
      <span className="mt-1 text-center text-[11px] leading-[1.15] text-foreground whitespace-pre-line">{label}{chevron ? <ChevronDown className="inline h-3 w-3 ml-0.5 -mb-0.5 text-muted-foreground" /> : null}</span>
    </button>
  </WithTip>
));
LargeButton.displayName = 'LargeButton';

export const SmallButton = React.forwardRef(({ icon: Icon, label, onClick, active, disabled, tip, testId, chevron, className = '', iconClass = '', children, ...rest }, ref) => (
  <WithTip tip={tip}>
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseDown={keepFocus}
      disabled={disabled}
      className={`h-[22px] px-1.5 rounded-sm flex items-center gap-1.5 transition-colors duration-150 ${hover} disabled:opacity-40 disabled:cursor-not-allowed text-foreground ${active ? 'bg-primary/15 ring-1 ring-primary/40' : ''} ${className}`}
      data-testid={testId}
      data-state={active ? 'on' : 'off'}
      {...rest}
    >
      {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} strokeWidth={1.75} />}
      {children}
      {label && <span className="text-[11px] whitespace-nowrap">{label}</span>}
      {chevron && <ChevronDown className="h-3 w-3 text-muted-foreground" />}
    </button>
  </WithTip>
));
SmallButton.displayName = 'SmallButton';

export const IconToggle = ({ icon: Icon, active, onClick, tip, testId, disabled, label }) => (
  <SmallButton icon={Icon} active={active} onClick={onClick} tip={tip} testId={testId} disabled={disabled} className="w-[24px] justify-center px-0" label={label} />
);

export const Stack = ({ children, className = '' }) => <div className={`flex flex-col gap-[1px] ${className}`}>{children}</div>;
export const Row = ({ children, className = '' }) => <div className={`flex items-center gap-[1px] ${className}`}>{children}</div>;

/** Small dropdown menu trigger (label + chevron) */
export const MenuButton = ({ icon, label, tip, testId, children, large, active, disabled, align = 'start', contentClass = '', onOpenChange }) => (
  <DropdownMenu modal={false} onOpenChange={onOpenChange}>
    <DropdownMenuTrigger asChild>
      {large ? <LargeButton icon={icon} label={label} tip={tip} testId={testId} chevron active={active} disabled={disabled} /> : <SmallButton icon={icon} label={label} tip={tip} testId={testId} chevron active={active} disabled={disabled} />}
    </DropdownMenuTrigger>
    <DropdownMenuContent align={align} className={`bg-popover text-popover-foreground border border-border shadow-md min-w-[200px] z-[80] ${contentClass}`} data-ribbon onCloseAutoFocus={(e) => e.preventDefault()}>
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** Popover trigger (for galleries / pickers) */
export const PopButton = ({ icon, label, tip, testId, children, large, active, disabled, width = 'w-[360px]', align = 'start', open, onOpenChange, contentClass = '' }) => (
  <Popover modal={false} open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>
      {large ? <LargeButton icon={icon} label={label} tip={tip} testId={testId} chevron active={active} disabled={disabled} /> : <SmallButton icon={icon} label={label} tip={tip} testId={testId} chevron active={active} disabled={disabled} />}
    </PopoverTrigger>
    <PopoverContent align={align} className={`${width} p-2 bg-popover text-popover-foreground border border-border shadow-md z-[80] ${contentClass}`} data-ribbon onOpenAutoFocus={(e) => e.preventDefault()}>
      {children}
    </PopoverContent>
  </Popover>
);

export const MenuItemRow = ({ icon: Icon, label, onClick, active, shortcut, disabled, testId, description }) => (
  <button type="button" onClick={onClick} disabled={disabled} className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] ${hover} disabled:opacity-40 ${active ? 'bg-primary/10' : ''}`} data-testid={testId}>
    {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
    <span className="flex-1">{label}{description && <span className="block text-[11px] text-muted-foreground">{description}</span>}</span>
    {active && <span className="text-primary">✓</span>}
    {shortcut && <span className="text-[11px] text-muted-foreground">{shortcut}</span>}
  </button>
);

export const RibbonNumber = ({ value, onChange, min = 0, max = 999, step = 1, width = 'w-14', suffix, testId, title }) => {
  const [local, setLocal] = React.useState(String(value ?? ''));
  React.useEffect(() => setLocal(String(value ?? '')), [value]);
  // Enter commits and blurs; applying to edited text re-focuses the text box, which blurs this input *inside* the first
  // commit — the guard keeps that nested blur from committing a second time (with the text selection already gone).
  const committing = React.useRef(false);
  const commit = () => {
    if (committing.current) return;
    committing.current = true;
    try {
      const n = parseFloat(local);
      if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
      else setLocal(String(value ?? ''));
    } finally { committing.current = false; }
  };
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={local}
        title={title}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); } if (e.key === 'ArrowUp') { e.preventDefault(); onChange(Math.min(max, (parseFloat(local) || 0) + step)); } if (e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(min, (parseFloat(local) || 0) - step)); } }}
        className={`${width} h-[22px] rounded-sm border border-input bg-background px-1.5 text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring`}
        data-testid={testId}
      />
      {suffix && <span className="absolute right-1.5 top-[3px] text-[10px] text-muted-foreground pointer-events-none">{suffix}</span>}
    </div>
  );
};

export const SwatchButton = React.forwardRef(({ icon: Icon, color, tip, onClick, testId, label, className = '', ...rest }, ref) => (
  <WithTip tip={tip}>
    <button ref={ref} type="button" onClick={onClick} className={`h-[22px] px-1.5 rounded-sm flex items-center gap-1 ${hover} ${className}`} data-testid={testId} {...rest}>
      <span className="flex flex-col items-center">
        <Icon className="h-3.5 w-3.5 text-foreground" strokeWidth={1.75} />
        <span className="block h-[3px] w-4 rounded-sm mt-px border border-border/40" style={color ? { background: color } : { backgroundImage: 'repeating-linear-gradient(45deg,#ccc 0 2px,#fff 2px 4px)' }} />
      </span>
      {label && <span className="text-[11px]">{label}</span>}
    </button>
  </WithTip>
));
SwatchButton.displayName = 'SwatchButton';

/** Font-size style combobox: editable number + list */
export const SizeCombo = ({ value, onChange, options = [], width = 'w-[52px]', testId, title = 'Font Size', suffix }) => {
  const [local, setLocal] = React.useState(String(value ?? ''));
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => setLocal(String(value ?? '')), [value]);
  const committing = React.useRef(false); // see RibbonNumber: no nested commit from the blur the apply itself causes
  const commit = (v) => {
    if (committing.current) return;
    committing.current = true;
    try { const n = parseFloat(v); if (!Number.isNaN(n)) onChange(n); else setLocal(String(value ?? '')); } finally { committing.current = false; }
  };
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <div className={`flex items-stretch h-[22px] rounded-sm border border-input bg-background overflow-hidden ${width}`}>
        <input value={local} title={title} onChange={(e) => setLocal(e.target.value)} onBlur={() => commit(local)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(local); e.currentTarget.blur(); } }} className="flex-1 min-w-0 bg-transparent px-1.5 text-[12px] text-foreground focus:outline-none" data-testid={testId} />
        {suffix && <span className="self-center text-[10px] text-muted-foreground pr-0.5">{suffix}</span>}
        <PopoverTrigger asChild>
          <button type="button" className={`w-4 flex items-center justify-center border-l border-input ${hover}`} aria-label="Choose size" data-testid={testId ? `${testId}-menu` : undefined}><ChevronDown className="h-3 w-3" /></button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="w-[72px] p-1 max-h-[260px] overflow-y-auto bg-popover text-popover-foreground border border-border shadow-md z-[90]" data-ribbon data-keep-editing onOpenAutoFocus={(e) => e.preventDefault()}>
        {options.map((o) => (
          <button key={o} type="button" onMouseDown={keepFocus} onClick={() => { onChange(o); setOpen(false); }} className={`w-full text-left px-2 py-0.5 text-[12px] rounded-sm ${hover} ${o === value ? 'bg-primary/15' : ''}`} data-testid={testId ? `${testId}-option-${String(o).replace('.', '-')}` : undefined}>{o}</button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

/** Publisher-like hover grid to pick rows x cols */
export const TableGridPicker = ({ rows = 8, cols = 10, onPick }) => {
  const [hover_, setHover] = React.useState({ r: 0, c: 0 });
  return (
    <div className="p-1" data-testid="table-grid-picker">
      <div className="text-[12px] font-medium mb-1 px-1">{hover_.r && hover_.c ? `${hover_.c} x ${hover_.r} Table` : 'Insert Table'}</div>
      <div className="grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${cols}, 16px)` }} onMouseLeave={() => setHover({ r: 0, c: 0 })}>
        {Array.from({ length: rows * cols }).map((_, i) => {
          const r = Math.floor(i / cols) + 1, c = (i % cols) + 1;
          const on = r <= hover_.r && c <= hover_.c;
          return <button key={i} type="button" onMouseEnter={() => setHover({ r, c })} onClick={() => onPick(r, c)} className={`h-4 w-4 border ${on ? 'bg-primary/30 border-primary' : 'bg-background border-border'}`} aria-label={`${c} by ${r}`} data-testid={`table-grid-cell-${r}-${c}`} />;
        })}
      </div>
    </div>
  );
};
